// All frontend error reporters funnel into the same queue and Glitchtip paths.
// The toast stays independent of Sentry initialization and network availability.
// `tags` lands on the Sentry event; the toast combines the caller's label with
// `component`/`action`/`source` metadata for its context.
//
// Use `reportError(label, cause, tags)` inside a `catch` where the original
// cause is in hand so Sentry's LinkedErrors integration preserves its chain.
// Use `reportSynthError(msg, tags)` for synthetic failures with no original
// cause (for example, an `if (!res.ok)` branch).
//
// `reportLayerError` surfaces a structured WandError from the backend
// (typically a node_failed / layer_failed event) with its WandErrorFrame trace.

import * as Sentry from '@sentry/sveltekit';
import { Thrift } from 'thrift';
import {
  WandAuthorizationError,
  WandError,
  WandExecutionError,
  WandExecutionLimitExceeded,
  WandValidationError,
  type WandErrorFrame,
} from '@helinks/wand-api-thrift/wand_types';
import { log } from '$lib/log';

export interface ErrorEntry {
  id: number;
  timestamp: number;
  message: string;
  context?: string;
  code?: string;
  field?: string;
  nodeId?: string;
  portId?: string;
  trace: WandErrorFrame[];
  frontend: boolean;
}

type CauseDescription = Omit<ErrorEntry, 'id' | 'timestamp' | 'context' | 'frontend'>;
type NodeTarget = { nodeId?: string; portId?: string };
type Attempt<T> = { ok: true; value: T } | { ok: false; cause: unknown };
type DescribedCause = {
  description: CauseDescription;
  details: Attempt<NodeTarget> | null;
};

let nextId = 1;
const queue = $state<ErrorEntry[]>([]);
const nodeTargets = new WeakMap<WandError, Attempt<NodeTarget>>();
let reportingCaptureFailure = false;

export function getErrors(): readonly ErrorEntry[] {
  return queue;
}


function parseNodeTarget(detailsJson: string): Attempt<NodeTarget> {
  try {
    const raw: unknown = JSON.parse(detailsJson);
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      throw new Error('detailsJson must decode to an object');
    }
    const fields = raw as Record<string, unknown>;
    if (fields.nodeId !== undefined && typeof fields.nodeId !== 'string') {
      throw new Error('detailsJson.nodeId must be a string');
    }
    if (fields.portId !== undefined && typeof fields.portId !== 'string') {
      throw new Error('detailsJson.portId must be a string');
    }
    return {
      ok: true,
      value: { nodeId: fields.nodeId as string | undefined, portId: fields.portId as string | undefined },
    };
  } catch (cause) {
    return { ok: false, cause };
  }
}

export function nodeTarget(e: WandError): Attempt<NodeTarget> {
  const cached = nodeTargets.get(e);
  if (cached) return cached;
  const result: Attempt<NodeTarget> = e.detailsJson
    ? parseNodeTarget(e.detailsJson)
    : { ok: true, value: {} };
  nodeTargets.set(e, result);
  return result;
}

function describe(e: unknown): DescribedCause {
  if (e instanceof WandValidationError) {
    return {
      description: {
        message: e.message,
        code: e.code,
        field: e.field,
        nodeId: e.nodeId,
        portId: e.portId,
        trace: [],
      },
      details: null,
    };
  }
  if (e instanceof WandAuthorizationError) {
    return { description: { message: e.message, code: e.code, trace: [] }, details: null };
  }
  if (e instanceof WandExecutionError) {
    return {
      description: { message: e.message, code: e.code, trace: e.nested?.trace ?? [] },
      details: null,
    };
  }
  if (e instanceof WandExecutionLimitExceeded) {
    return { description: { message: e.message, trace: [] }, details: null };
  }
  if (e instanceof WandError) {
    const details = nodeTarget(e);
    return {
      description: {
        message: e.message,
        code: e.code,
        ...(details.ok ? details.value : {}),
        trace: e.trace ?? [],
      },
      details,
    };
  }
  if (e instanceof Thrift.TApplicationException) {
    return { description: { message: e.message, trace: [] }, details: null };
  }
  if (e instanceof Error) {
    return { description: { message: e.message, trace: [] }, details: null };
  }
  if (typeof e === 'string') {
    return { description: { message: e, trace: [] }, details: null };
  }
  if (typeof e === 'number' || typeof e === 'boolean' || typeof e === 'bigint' || typeof e === 'symbol') {
    return { description: { message: String(e), trace: [] }, details: null };
  }
  return { description: { message: 'Unexpected non-Error failure', trace: [] }, details: null };
}

export function describeCause(e: unknown): CauseDescription {
  return describe(e).description;
}

function pushEntry(
  cause: unknown,
  context: string | undefined,
  frontend: boolean,
): unknown | undefined {
  const described = describe(cause);
  queue.push({ id: nextId++, timestamp: Date.now(), ...described.description, context, frontend });
  if (described.details && !described.details.ok) {
    const diagnostic = describeCause(described.details.cause);
    queue.push({
      id: nextId++,
      timestamp: Date.now(),
      message: `Could not decode backend error details: ${diagnostic.message}`,
      context: context ? `${context}: error details` : 'backend error details',
      trace: [],
      frontend: true,
    });
    return described.details.cause;
  }
  return undefined;
}

function deriveContext(tags: Record<string, string>): string | undefined {
  if (tags.context) return tags.context;
  if (tags.component && tags.action) return `${tags.component}.${tags.action}`;
  if (tags.component) return tags.component;
  if (tags.action) return tags.action;
  if (tags.source) return tags.source;
  return undefined;
}

function errorContext(label: string, tags: Record<string, string>): string {
  const tagContext = deriveContext(tags);
  return tagContext ? `${tagContext}: ${label}` : label;
}

function asError(cause: unknown): Error {
  if (cause instanceof Error) return cause;
  return new Error(describeCause(cause).message, { cause });
}

/** Internal reporter-core recovery when Sentry's capture sink itself fails. */
function reportCaptureFailure(cause: unknown): void {
  if (reportingCaptureFailure) return;
  reportingCaptureFailure = true;
  try {
    const message = cause instanceof Error ? cause.message : 'Sentry capture failed';
    queue.push({
      id: nextId++,
      timestamp: Date.now(),
      message: `Could not send error telemetry: ${message}`,
      context: 'errors.captureException',
      trace: [],
      frontend: true,
    });
    log.error('[wand-error]', 'Sentry capture failed', cause);
  } finally {
    reportingCaptureFailure = false;
  }
}

function captureException(error: Error, tags: Record<string, string>): void {
  try {
    Sentry.captureException(error, { tags });
  } catch (captureFailure) {
    reportCaptureFailure(captureFailure);
  }
}

function captureDetailsFailure(cause: unknown, tags: Record<string, string>): void {
  captureException(
    new Error('Could not decode backend error details', { cause: asError(cause) }),
    { ...tags, source: 'errors.detailsJson' },
  );
}

export function reportError(
  label: string,
  cause: unknown,
  tags: Record<string, string>,
): void {
  const malformedDetails = pushEntry(cause, errorContext(label, tags), true);
  log.error('[wand-error]', label, cause);
  const captureTags = malformedDetails === undefined
    ? tags
    : { ...tags, errorDetails: 'malformed' };
  captureException(new Error(label, { cause: asError(cause) }), captureTags);
  if (malformedDetails !== undefined) captureDetailsFailure(malformedDetails, tags);
}

export function reportSynthError(
  msg: string,
  tags: Record<string, string>,
): void {
  const error = new Error(msg);
  pushEntry(error, deriveContext(tags), true);
  log.error('[wand-error]', msg);
  captureException(error, tags);
}

/** Surface a structured WandError carried by a backend execution event. */
export function reportLayerError(error: WandError, context?: string): void {
  const malformedDetails = pushEntry(error, context, false);
  log.error('[wand-error]', context ?? '', error.message, error.trace);
  const tags = { component: 'backend', context: context ?? 'unspecified' };
  const captureTags = malformedDetails === undefined
    ? tags
    : { ...tags, errorDetails: 'malformed' };
  captureException(new Error(error.message, { cause: error }), captureTags);
  if (malformedDetails !== undefined) captureDetailsFailure(malformedDetails, tags);
}

export function dismissError(id: number): void {
  const idx = queue.findIndex((e) => e.id === id);
  if (idx >= 0) queue.splice(idx, 1);
}

export function clearErrors(): void {
  queue.length = 0;
}

// Safety net for anything that escapes a local recovery. Sentry's GlobalHandlers
// integration is disabled in hooks.client.ts so this is the sole Glitchtip path
// for forgotten synchronous errors and rejected promises.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (e) => {
    reportError('unhandled promise rejection', e.reason, { source: 'unhandledrejection' });
    e.preventDefault();
  });
  window.addEventListener('error', (e) => {
    const cause = e.error ?? new Error(e.message || 'unknown');
    reportError('uncaught window error', cause, { source: 'window.onerror' });
  });
}
